/* istanbul ignore file */
// Component tested in ContentScheduleDialog
import React from 'react';

import isEmpty from 'lodash/isEmpty';

import { margin } from '@lumapps/classnames';
import { FormDateTimeZonePickerField } from '@lumapps/lumx-date-time/components/FormDateTimeZonePickerField';
import { useFormContext } from '@lumapps/lumx-forms';
import { InputHelper, Kind } from '@lumapps/lumx/react';
import { useSelector } from '@lumapps/redux/react';
import { GLOBAL, useTranslate } from '@lumapps/translations';
import { currentLanguageSelector } from '@lumapps/translations/ducks/selectors';
import { ObjectValues } from '@lumapps/utils/types/ObjectValues';

import { CONTENT_DATE_FIELD_NAMES } from '../../constants';
import { DESIGNER } from '../../keys';

import './index.scss';

export interface FormContentDateFieldProps {
    fieldName: ObjectValues<typeof CONTENT_DATE_FIELD_NAMES>;
    scope: string;
    /**
     * Make the expiration date required ("mandatory end date" content type option), so the
     * form cannot be submitted without one. Matches `CustomContentType.isEndDateMandatory`.
     */
    isEndDateMandatory?: boolean;
    /**
     * Maximum delay (in months from now) allowed for the expiration date when it is mandatory.
     * Matches `CustomContentType.endDateDelta`.
     */
    endDateDelta?: number;
}

const CLASSNAME = 'form-content-date-field';

/**
 *  Content field allowing to define a scheduled date or expiration date
 *  WARNING: This should be inside a Form context
 */
export const FormContentDateField: React.FC<FormContentDateFieldProps> = ({
    fieldName,
    scope,
    isEndDateMandatory,
    endDateDelta,
}) => {
    const { translateKey } = useTranslate();
    const { getValues, watch } = useFormContext();

    const lang = useSelector(currentLanguageSelector);

    const isExpirationDate = fieldName === CONTENT_DATE_FIELD_NAMES.EXPIRED;
    const isExpirationDateMandatory = isExpirationDate && isEndDateMandatory;
    const fieldValue = watch(fieldName);
    const isFieldRequired = isExpirationDateMandatory || !!fieldValue?.data.date || !!fieldValue?.data?.time;

    const maxDate = React.useMemo(() => {
        if (isExpirationDateMandatory && endDateDelta && endDateDelta > 0) {
            const currentDate = new Date();
            const nextMonthDate = new Date(currentDate);
            nextMonthDate.setMonth(currentDate.getMonth() + endDateDelta);

            return nextMonthDate;
        }
        return undefined;
    }, [endDateDelta, isExpirationDateMandatory]);

    return (
        <div className={CLASSNAME}>
            <FormDateTimeZonePickerField
                name={fieldName}
                datePickerProps={{
                    label: isExpirationDate
                        ? translateKey(GLOBAL.EXPIRATION_DATE)
                        : translateKey(GLOBAL.PUBLICATION_DATE),
                    maxDate,
                }}
                timePickerProps={{
                    label: translateKey(GLOBAL.TIME),
                }}
                timeZonePickerProps={{
                    withTimeZoneName: true,
                    icon: null,
                }}
                withDate
                disableOptionsInThePast={isExpirationDate}
                label=""
                language={lang}
                scope={scope}
                isRequired={isFieldRequired}
                shouldFallbackToCurrentDateTime={false}
                controllerProps={{
                    rules: {
                        validate: {
                            error: (value) => {
                                // When only date OR time is filled
                                if (
                                    isEmpty(value?.timestamp) &&
                                    (isFieldRequired || !!value?.data.date || !!value?.data?.time)
                                ) {
                                    return false;
                                }

                                if (isExpirationDate) {
                                    const scheduledField = getValues(CONTENT_DATE_FIELD_NAMES.SCHEDULED);

                                    // If not mandatory and undefined, then we validate
                                    if (!value && !isExpirationDateMandatory) {
                                        return true;
                                    }

                                    // When expiration date is before or same as scheduled date
                                    if (
                                        !!scheduledField?.timestamp &&
                                        (value?.timestamp < scheduledField.timestamp ||
                                            value?.timestamp === scheduledField.timestamp)
                                    ) {
                                        return translateKey(DESIGNER.EXPIRATION_DATE_BEFORE_SCHEDULE_ERROR);
                                    }

                                    // When expiration is in the past (respecting the time zone)
                                    const expirationDate = new Date(value?.timestamp);
                                    if (expirationDate < new Date()) {
                                        return translateKey(DESIGNER.EXPIRATION_DATE_DATE_TOO_OLD_ERROR);
                                    }
                                }

                                return true;
                            },
                        },
                    },
                }}
            />
            {isExpirationDateMandatory && (
                <InputHelper className={margin('top', 'regular')} kind={Kind.info}>
                    {translateKey(DESIGNER.MANDATORY_END_DATE_HELPER)}
                </InputHelper>
            )}
        </div>
    );
};
